import { useRef, useState } from 'react';
import Link from 'next/link';
import { RiArrowRightLine, RiCheckLine } from 'react-icons/ri';

import { CONSENT_VERSION } from '@/shared/config/consent';

import { PRIVACY_KEY } from './cookie-notice';

export const Consultation = () => {
  const [complete, setComplete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const attempt = useRef<{ json: string; requestId: string }>();
  return (
    <section className="consultation" id="consultation">
      <div className="consultation__grid container">
        <div>
          <p className="eyebrow">КОНСУЛЬТАЦИЯ СПЕЦИАЛИСТА</p>
          <h2>
            Подберём станок
            <br />
            под вашу задачу.
          </h2>
          <p>
            Расскажите, что вы производите.
            <br />
            Поможем с выбором оборудования.
          </p>
        </div>
        {complete ? (
          <div className="consultation__result" role="status">
            <RiCheckLine />
            <h3>Заявка принята.</h3>
            <p>Спасибо! Свяжемся с вами по указанному телефону, чтобы обсудить задачу.</p>
            <button
              onClick={() => {
                setComplete(false);
                attempt.current = undefined;
              }}
              type="button"
            >
              Вернуться к форме
            </button>
          </div>
        ) : (
          <form
            aria-label="Заявка на подбор станка"
            aria-busy={busy}
            onSubmit={async (event) => {
              event.preventDefault();
              if (busy) return;
              const fields = new FormData(event.currentTarget);
              const values = {
                name: String(fields.get('name') || '').trim(),
                phone: String(fields.get('phone') || ''),
                message: String(fields.get('message') || '').trim(),
                consent: fields.get('consent') === 'on',
                consentVersion: CONSENT_VERSION,
                sourcePage: location.pathname.slice(0, 500),
                website: String(fields.get('website') || ''),
              };
              if (!/^\+?\d{10,15}$/.test(values.phone.replace(/[\s()-]/g, ''))) {
                setError('Проверьте номер телефона: укажите код страны и номер.');
                return;
              }
              const json = JSON.stringify(values);
              if (attempt.current?.json !== json) attempt.current = { json, requestId: crypto.randomUUID() };
              setBusy(true);
              setError('');
              try {
                const response = await fetch('/api/v1/inquiries', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ ...values, requestId: attempt.current.requestId }),
                  signal: AbortSignal.timeout(20000),
                });
                const receipt = (await response.json().catch(() => null)) as { accepted?: boolean } | null;
                if (!response.ok || receipt?.accepted !== true)
                  throw new Error(
                    response.status === 429
                      ? 'Слишком много запросов. Пожалуйста, попробуйте через минуту.'
                      : 'Не удалось отправить заявку. Попробуйте ещё раз или позвоните +7 (343) 363-05-07.',
                  );
                setComplete(true);
                try {
                  if (
                    process.env.NODE_ENV === 'production' &&
                    ['domstankov.com', 'www.domstankov.com'].includes(location.hostname) &&
                    localStorage.getItem(PRIVACY_KEY) === 'all' &&
                    typeof window.ym === 'function'
                  )
                    window.ym(96886492, 'reachGoal', 'consultation_sent');
                } catch {}
              } catch (cause) {
                setError(
                  cause instanceof Error && cause.name !== 'TimeoutError'
                    ? cause.message
                    : 'Связь прервалась. Повторите отправку — заявка не продублируется.',
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              <span>Ваше имя</span>
              <input
                name="name"
                placeholder="Как к вам обращаться"
                autoComplete="name"
                required
                minLength={2}
                maxLength={120}
                disabled={busy}
              />
            </label>
            <label>
              <span>Телефон</span>
              <input
                name="phone"
                type="tel"
                placeholder="+7 (___) ___-__-__"
                autoComplete="tel"
                required
                minLength={10}
                maxLength={30}
                disabled={busy}
              />
            </label>
            <label>
              <span>Что нужно вашему производству?</span>
              <textarea
                name="message"
                rows={3}
                maxLength={4000}
                placeholder="Материал, операции, размеры заготовок — если уже знаете"
                disabled={busy}
              />
            </label>
            <label className="form-trap" aria-hidden="true">
              Ваш сайт
              <input name="website" tabIndex={-1} autoComplete="off" />
            </label>
            <label className="consent">
              <input name="consent" type="checkbox" required disabled={busy} />
              <span>
                Согласен на{' '}
                <Link href="/personal-data" rel="noopener noreferrer" target="_blank">
                  обработку персональных данных
                </Link>
              </span>
            </label>
            <p style={{ fontSize: 12, marginTop: 8 }}>
              <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                Политика обработки персональных данных
              </Link>
            </p>
            {error && (
              <p className="consultation__error" role="alert">
                {error}
              </p>
            )}
            <button className="precision-button precision-button--white" type="submit" disabled={busy}>
              {busy ? 'Отправляем…' : 'Получить предложение'}
              <RiArrowRightLine />
            </button>
          </form>
        )}
      </div>
      <span className="consultation__decoration" aria-hidden>
        +
      </span>
    </section>
  );
};
